import React from 'react';
import { ShieldCheck, CreditCard, Truck } from 'lucide-react';

interface FooterProps {
  onBrandChange?: (brand: string) => void;
  onScrollTo?: (id: string) => void;
}

const Footer: React.FC<FooterProps> = ({ onBrandChange, onScrollTo }) => {
  
  const handleScroll = (id: string) => {
    if (onScrollTo) {
      onScrollTo(id);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleBrandClick = (brand: string) => {
    if (onBrandChange) {
      onBrandChange(brand);
    }
    // Assume que ao trocar de marca, o usuário quer ver o catálogo
    handleScroll('categorias');
  };

  return (
    <footer id="footer" className="bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl pt-12 pb-8 border-t border-white/50 dark:border-slate-800 transition-colors duration-300 mt-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-10">
          
          {/* Brand */}
          <div className="col-span-1 md:col-span-1">
            <div className="flex items-center space-x-3 mb-5">
              <img 
                 src="https://res.cloudinary.com/dpp6qilax/image/upload/v1765829420/envato-labs-image-edit_5_cyykpt.png" 
                 alt="FraldaTech Logo" 
                 className="w-14 h-14 rounded-full shadow-md border-2 border-store-purple/10 object-cover bg-white"
              />
              <div className="flex flex-col">
                <span className="text-2xl font-black text-gray-800 dark:text-white leading-none">
                  <span className="text-store-purple">Fralda</span><span className="text-store-accent">Tech</span>
                </span>
                <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-store-purple mt-1">Especialistas</span>
              </div>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed mb-6 font-medium">
              Monitoramos os preços das melhores fraldas Pampers, Huggies e Babysec para garantir economia e conforto para o seu bebê.
            </p>
          </div>

          {/* Links 1 */}
          <div>
            <h3 className="font-bold text-gray-800 dark:text-gray-200 mb-6 text-sm uppercase tracking-wide border-l-4 border-store-purple pl-3">Navegação</h3>
            <ul className="space-y-3 text-sm text-gray-600 dark:text-gray-300 font-medium">
              <li><button onClick={() => handleScroll('home')} className="hover:text-store-purple transition-colors hover:translate-x-1 inline-block text-left">Início</button></li>
              <li><button onClick={() => handleScroll('categorias')} className="hover:text-store-purple transition-colors hover:translate-x-1 inline-block text-left">Catálogo Completo</button></li>
              <li><button onClick={() => handleScroll('ofertas')} className="hover:text-store-purple transition-colors hover:translate-x-1 inline-block text-left">Ofertas do Dia</button></li>
              <li><button onClick={() => handleScroll('guia')} className="hover:text-store-purple transition-colors hover:translate-x-1 inline-block text-left">Guia de Fraldas</button></li>
            </ul>
          </div>

          {/* Links 2 */}
          <div>
            <h3 className="font-bold text-gray-800 dark:text-gray-200 mb-6 text-sm uppercase tracking-wide border-l-4 border-store-accent pl-3">Marcas</h3>
            <ul className="space-y-3 text-sm text-gray-600 dark:text-gray-300 font-medium">
              <li><button onClick={() => handleBrandClick('Pampers')} className="hover:text-store-purple transition-colors hover:translate-x-1 inline-block text-left">Pampers</button></li>
              <li><button onClick={() => handleBrandClick('Huggies')} className="hover:text-red-600 transition-colors hover:translate-x-1 inline-block text-left">Huggies</button></li>
              <li><button onClick={() => handleBrandClick('Babysec')} className="hover:text-cyan-600 transition-colors hover:translate-x-1 inline-block text-left">Babysec</button></li>
              <li><button onClick={() => handleBrandClick('Mamypoko')} className="hover:text-blue-700 transition-colors hover:translate-x-1 inline-block text-left">Mamypoko</button></li>
            </ul>
          </div>

          {/* Trust Badges */}
          <div>
            <h3 className="font-bold text-gray-800 dark:text-gray-200 mb-6 text-sm uppercase tracking-wide border-l-4 border-green-500 pl-3">Segurança</h3>
            <div className="space-y-4">
              <div className="flex items-center space-x-3 text-sm text-gray-600 dark:text-gray-300 font-medium bg-gray-50 p-2 rounded-lg">
                <ShieldCheck className="text-store-purple" size={20} />
                <span>Links Verificados Amazon</span>
              </div>
              <div className="flex items-center space-x-3 text-sm text-gray-600 dark:text-gray-300 font-medium bg-gray-50 p-2 rounded-lg">
                <CreditCard className="text-store-purple" size={20} />
                <span>Compra 100% Segura</span>
              </div>
              <div className="flex items-center space-x-3 text-sm text-gray-600 dark:text-gray-300 font-medium bg-gray-50 p-2 rounded-lg">
                <Truck className="text-store-purple" size={20} />
                <span>Entrega Rápida</span>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-200 dark:border-slate-800 pt-8 flex flex-col md:flex-row justify-between items-center text-xs text-gray-400 dark:text-gray-500 font-medium">
          <p>© {new Date().getFullYear()} FraldaTech. Todos os direitos reservados.</p>
          <div className="flex space-x-6 mt-4 md:mt-0">
            <button className="hover:text-store-purple dark:hover:text-gray-300 transition-colors">Termos de Uso</button>
            <button className="hover:text-store-purple dark:hover:text-gray-300 transition-colors">Política de Privacidade</button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;